import { Actions as BA } from 'vuex-smart-module'
import { CollectionMeta, SortablePositions } from '@/typings/common'
import { GetNameId } from '@/typings/utils'
import { CrudActionParams } from './typings/typings'
import { Adapter } from './Adapter'
import { State } from './State'
import { Getters } from './Getters'
import { Mutations } from './Mutations'


export abstract class Actions<
  T,
  TT,
  S extends State<T, TT> = State<T, TT>,
  G extends Getters<T, TT, S> = Getters<T, TT, S>,
  M extends Mutations<T, TT, S> = Mutations<T, TT, S>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  A extends Actions<T, TT, S, G, M, A> = Actions<T, TT, S, G, M, any>,
> extends BA<S, G, M, A> {
  // @ts-ignore: что-то с типом при наследовании, подставим его сами
  // protected readonly mutations!: Committer<Mutations<T, TT, S>>

  // @ts-ignore: что-то с типом при наследовании, подставим его сами
  // protected readonly actions!: Actions<T, TT, S, G, M, A>

  protected abstract readonly adapter: Adapter<T, TT>

  protected abstract readonly idName: GetNameId<T>

  public async getAll(
    params: Pick<Required<CrudActionParams>, 'payload'> & Omit<CrudActionParams, 'id'>,
  ): Promise<TT[]> {
    const { payload } = params || {}
    const response = await this.adapter.getAll(payload, params)
    if (response.errors) return Promise.reject(response.errors)
    let data: TT[] = []
    let meta: CollectionMeta = {}
    if (response.data) {
      data = response.data
    } else if (Array.isArray(response)) {
      data = response
    }

    if (response.meta) {
      meta = response.meta
    }

    const mutations = this.mutations as unknown as M

    mutations.setList(data)
    mutations.setFilters(payload)
    mutations.setListMeta(meta)

    return this.state.list || data || []
  }

  public async getNextPage(
    params: Omit<CrudActionParams, 'id'|'payload'>,
  ): Promise<TT[]> {
    const payload = {
      ...this.state.filters,
      page: this.getters.pagination.page + 1,
    }
    const response = await this.adapter.getAll(payload, params)
    if (response.errors) return Promise.reject(response.errors)
    const { data, meta } = response

    const mutations = this.mutations as unknown as M

    const list = this.state.list ? [...this.state.list, ...data] : data

    mutations.setList(list)
    mutations.setFilters(payload)
    mutations.setListMeta(meta)

    return this.state.list || data || []
  }

  public async getOne(params: CrudActionParams): Promise<T|void> {
    // @ts-ignore: хз что за типизация, но должно быть так
    const value = this.state.single?.[this.idName] as string | number
    const mutations = this.mutations as unknown as M

    // eslint-disable-next-line eqeqeq
    if (value && value != params.id) {
      mutations.setOne(undefined)
    }

    const response = await this.adapter.getOne(params.id, params)
    if (response.errors) return Promise.reject(response.errors)
    const { data } = response

    mutations.setOne(data)

    return data
  }

  public async create(params: Pick<CrudActionParams, 'payload'>): Promise<T|void> {
    const { payload = {} } = params
    const response = await this.adapter.create(payload, params)
    if (response.errors) return Promise.reject(response.errors)
    const { data } = response

    // @ts-ignore: хз что за типизация, но должно быть так
    const id = data?.[this.idName] as string | number

    if (!id) {
      // eslint-disable-next-line no-console
      console.error('Actions#create do not returned id')
      return response as unknown as T
    }

    return this.actions.getOne({ ...params, id })
  }

  public async update(
    params: Pick<Required<CrudActionParams>, 'id'|'payload'> & CrudActionParams,
  ): Promise<T|void> {
    const response = await this.adapter.update(params.id, params.payload, params)
    if (response.errors) return Promise.reject(response.errors)
    return this.actions.getOne(params)
  }

  public delete(params: CrudActionParams): Promise<unknown> {
    return this.adapter.delete(params.id, params)
  }

  public deleteMultiple(
    params: Required<Pick<CrudActionParams<TT[]>, 'payload'>> & Omit<CrudActionParams<TT[]>, 'id'>,
  ): Promise<unknown> {
    return this.adapter.deleteMultiple(params.payload, params)
  }

  public updatePositions(params: { payload: SortablePositions }): Promise<unknown> {
    return this.adapter.updatePositions(params.payload, params)
  }

  public readonly logout = {
    root: true,
    handler(this: Actions<T, TT, S, G, M, A>): void {
      const mutations = this.mutations as unknown as M
      mutations.logout()
    },
  };
}
